import { sentryCaptureError } from 'services/sentry/capture';
import { useAppSelector } from 'store/hooks';

import { RESERVATION_KEYS } from '../translationKeys';
import type { ReservationRootState } from '../types';
import { ReservationCardStatus } from '../types';

/**
 * Reads `two_stage_deposit_enabled` from deposit widget settings.
 *
 * Shaped like `kycCardVerificationSelector` (`store/slices/deposit.ts:221`) but living inside the
 * feature, so `deposit.ts` needs no edit.
 */
export const twoStageDepositSelector = (state: ReservationRootState): boolean =>
  state.deposit?.widgetSettings?.two_stage_deposit_enabled || false;

/** Payout counterpart, mirroring `withdrawal.ts:241`. */
export const twoStageDepositPayoutSelector = (state: ReservationRootState): boolean =>
  state.withdrawal?.widgetSettings?.two_stage_deposit_enabled || false;

/** Whether the player's account has passed KYC — `cashier_settings.user_verified`. */
export const isUserVerifiedSelector = (state: ReservationRootState): boolean =>
  state.deposit?.widgetSettings?.user_verified || false;

/**
 * Legacy `kyc_card_verification` flag — read for the misconfiguration warning only.
 *
 * Nothing functional depends on it: the two features are independent and either may be removed.
 * If the field ever disappears this simply reads `false` and the warning goes quiet.
 */
const legacyCardVerificationSelector = (state: ReservationRootState): boolean =>
  state.deposit?.widgetSettings?.kyc_card_verification || false;

/**
 * Both flags on is a project misconfiguration: the two verification flows contradict each other.
 * BO enforces the rule; this keeps the widget deterministic (reservation wins) and makes the
 * misconfiguration visible instead of silent.
 *
 * Note there is no player-level targeting here. Whether a given deposit gets held is decided by
 * the backend — the widget only reacts to the hold it is told about.
 */
export const isReservationFlowActive = (state: ReservationRootState): boolean => {
  const isEnabled = twoStageDepositSelector(state);

  if (isEnabled && legacyCardVerificationSelector(state)) {
    sentryCaptureError(
      new Error(
        'Both two_stage_deposit_enabled and kyc_card_verification are enabled; preferring two_stage_deposit_enabled',
      ),
      { level: 'warning' },
    );
  }

  return isEnabled;
};

/**
 * Should this feature's inline card block be rendered in the deposit form?
 *
 * Depends only on this feature's own flags. The legacy `kyc_card_verification` block is rendered by
 * its own independent condition in the form, so removing either feature leaves the other intact.
 *
 * Account KYC gates it: the design shows no card block for an unverified account, because
 * confirming a card is pointless before the player is identified. The submit button still reads
 * «Зарезервувати» there — the explanation arrives on the next screen.
 */
export const useIsReservationCardBlockVisible = (): boolean =>
  useAppSelector((state: ReservationRootState) => twoStageDepositSelector(state) && isUserVerifiedSelector(state));

/**
 * Payout restriction contributed by this feature: withdraw only to a card verified for a deposit.
 *
 * Returns only this feature's answer. The call site ORs it with the legacy flag's own value, so
 * neither feature reads the other's state.
 */
export const useIsPayoutRestrictedByReservation = (): boolean => useAppSelector(twoStageDepositPayoutSelector);

/**
 * Copy override for the deposit submit button.
 *
 * With a hold the submit does not deposit — it reserves. The design reflects that in the label:
 * «Зарезервувати 250 USD» while verification is outstanding, «Поповнити 250 USD» once it is done.
 *
 * DECIDED: «Зарезервувати» also applies when `user_verified` is `false`. The API contract and the
 * workflow document both say DEPOSIT there, but Figma is the agreed source of truth for copy, and
 * DEPOSIT would be a lie — the funds are held either way, and the account gate is exactly what
 * causes the hold. Only the inline upload block is hidden in that state, not the reserve semantics.
 *
 * Returns `undefined` when the feature is inactive, so the shared button keeps its default copy.
 */
export const useReservationSubmitTextKey = (cardStatus: ReservationCardStatus | null): string | undefined => {
  const isActive = useAppSelector(isReservationFlowActive);
  const isUserVerified = useAppSelector(isUserVerifiedSelector);

  if (!isActive) {
    return undefined;
  }

  // Account verification is the primary gate: until it passes the funds are held regardless of the
  // card, so the button must not promise a plain deposit.
  if (!isUserVerified) {
    return RESERVATION_KEYS.submitReserve;
  }

  return cardStatus === ReservationCardStatus.verified
    ? RESERVATION_KEYS.submitDeposit
    : RESERVATION_KEYS.submitReserve;
};
